import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { UpdateTransactionResponseDto } from "@common/dto/transactions/update-transaction-response.dto";
import { Transaction } from "@common/types/transaction";
import { UploadReceiptDto } from "@common/dto/transactions/upload-receipt.dto";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { TransactionDetailsResponseDto } from "@common/dto/transactions/transaction-details-response.dto";
import { EagerLoaded } from "@common/types/base/orm";
import { MatchTransferDto } from "@common/dto/transactions/match-transfer.dto";
import { UpdateTransactionSplitsV2Dto } from "@common/dto/transactions/update-transaction-splits-v2.dto";
import { FinancialDocumentExtendedDto } from "@common/dto/financialDocument/financial-document-extended.dto";
import { TransactionColumn } from "@common/types/views/transactionViews";
import {
    TransactionListItem,
    TransactionSort,
} from "@common/schemas/transaction.schema";
import { transactionContract } from "@common/contracts/transaction.contract";
import { backendClient, unwrapResponse } from "./backendClient";
import { getBackendAPIClient } from "./backendAPIClient";
import { CSV_MIME, XLSX_MIME } from "./fileDownload";

const transactionBlobClient = getBackendAPIClient(transactionContract, "blob");

function transformTransaction(transaction: Transaction): Transaction {
    return {
        ...transaction,
        date: new Date(transaction.date),
        pairedTransfer: transaction.pairedTransfer
            ? transformTransaction(transaction.pairedTransfer)
            : transaction.pairedTransfer,
    };
}

export async function getTransactionDetails(
    id: number,
): Promise<TransactionDetailsResponseDto> {
    const data = unwrapResponse(
        await backendClient.get<TransactionDetailsResponseDto>(
            `/transactions/${id}`,
        ),
    );

    return {
        ...data,
        transaction: transformTransaction(data.transaction) as EagerLoaded<
            Transaction,
            | "transactionMatches"
            | "pairedTransfer"
            | "splits"
            | "category"
            | "journalEntries"
        >,
        origins: data.origins.map((origin) => ({
            ...origin,
            date: new Date(origin.date),
        })),
    };
}

export async function uploadTransactionReceipt(
    id: Transaction["id"],
    data: UploadReceiptDto,
) {
    const result: FinancialDocumentExtendedDto = unwrapResponse(
        await backendClient.post(`/transactions/${id}/receipt`, data),
    );

    return FinancialDocumentExtendedDto.deserialize(result);
}

export async function updateTransaction(
    transaction: TransactionListItem,
    data: UpdateTransactionDto,
): Promise<UpdateTransactionResponseDto> {
    const response: UpdateTransactionResponseDto = await unwrapResponse(
        await backendClient.patch(`/transactions/${transaction.id}`, data),
    );

    // Axios doesn't parse JSON date strings back into Date objects. Every
    // other read path (getTransactionDetails) runs transformTransaction for
    // this reason; this PATCH response needs the same treatment before it's
    // merged into the list/details caches, or `transaction.date` stays a
    // string and downstream `isValid(date)` checks (e.g. DateCell) fail,
    // rendering "-" for the date column.
    if ("transaction" in response) {
        return {
            ...response,
            transaction: transformTransaction(response.transaction),
        };
    }

    return response;
}

interface ExportTransactionsParams {
    workspaceId: string;
    filters: TransactionFiltersDto;
    columns?: TransactionColumn[];
    sort?: TransactionSort;
}

export async function getTransactionsCsv({
    workspaceId,
    filters,
    columns,
    sort,
}: ExportTransactionsParams) {
    const response = await transactionBlobClient.exportTransactionsCsv({
        params: { workspaceId },
        query: {
            ...filters,
            columns,
            sort,
        },
        extraHeaders: { Accept: CSV_MIME },
    });
    return response;
}

export async function getTransactionsXlsx({
    workspaceId,
    filters,
    columns,
    sort,
}: ExportTransactionsParams) {
    const response = await transactionBlobClient.exportTransactionsXlsx({
        params: { workspaceId },
        query: {
            ...filters,
            columns,
            sort,
        },
        extraHeaders: { Accept: XLSX_MIME },
    });
    return response;
}

export async function detachFinancialTransactionMatch(
    financialTransactionId: number,
    matchId: number,
) {
    await backendClient.post(
        `/transactions/${financialTransactionId}/detach/${matchId}`,
    );
}

interface CompactTransactionResponse {
    id: number;
}

interface UnmatchTransferCompactResponse {
    transaction: CompactTransactionResponse;
}

interface MatchTransferCompactResponse {
    originTransaction: CompactTransactionResponse;
    targetTransaction: CompactTransactionResponse;
}

export async function unmatchTransfer(
    financialTransactionId: number,
): Promise<CompactTransactionResponse> {
    const data: UnmatchTransferCompactResponse = unwrapResponse(
        await backendClient.delete(
            `/transactions/${financialTransactionId}/match-transfer`,
        ),
    );
    return { id: data.transaction.id };
}

export async function matchTransfer(
    originTransactionId: number,
    targetTransactionId: number,
): Promise<MatchTransferCompactResponse> {
    const data: MatchTransferCompactResponse = unwrapResponse(
        await backendClient.post(
            `/transactions/${originTransactionId}/match-transfer`,
            {
                targetTransactionId,
            } as MatchTransferDto,
        ),
    );
    return {
        originTransaction: { id: data.originTransaction.id },
        targetTransaction: { id: data.targetTransaction.id },
    };
}

export const updateTransactionSplitsV2 = async (
    transactionId: number,
    dto: UpdateTransactionSplitsV2Dto,
): Promise<Transaction> => {
    const response = await backendClient.put<Transaction>(
        `/transactions/${transactionId}/splits/v2`,
        dto,
    );
    return response.data;
};
